import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { trpc } from '@tg/app-core';
import { blockHas, lineKey, type Line, type WorkoutContent } from '@tg/domain';
import { useCatalog } from '@tg/app-core';
import { ClientPicker, useWhoList } from '../ui/ClientPicker';
import { toast } from '../ui/Toast';
import { Svg } from '../constructor/Svg';
import { DAYICON } from '../constructor/icons';
import { MON, RU, addDays, dowMon, today } from '../constructor/dates';
import { DatePicker, type Who } from './Calendar';
import { GroupConflicts, type Conflict, type Decisions } from '../constructor/GroupConflicts';
import { Cav } from '../ui/Cav';

/* Массовые действия с днями (bulk.js): галочка в шапке дня, Shift — диапазон; строка действий снизу.
   Копия и перенос раскладываются от первой даты по правилу; статус — как у исходного дня (опубликованная встаёт опубликованной); занятые дни —
   после предупреждения; любое действие отменяется из тоста (CON-4, CON-12, CON-14, CON-22). */

const I = {
  copy: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M10.5 5.5V3.8A1.3 1.3 0 0 0 9.2 2.5H3.8A1.3 1.3 0 0 0 2.5 3.8v5.4a1.3 1.3 0 0 0 1.3 1.3h1.7"/></svg>',
  move: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 8h10M9 4.5 12.5 8 9 11.5"/></svg>',
  trash: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 4.5h11M6.5 4.5V3a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1.5M4 4.5l.7 8.3a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9l.7-8.3"/></svg>',
  x: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>',
  cal: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="2.5" y="3.5" width="11" height="10" rx="1.5"/><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3"/></svg>',
  chev: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6.5l4 4 4-4"/></svg>',
  warn: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.5 14 13H2L8 2.5z"/><path d="M8 6.5v3M8 11.3v.2"/></svg>',
  plus: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>',
};

const plural = (n: number, a: string, b: string, c: string) => {
  const m = n % 10,
    h = n % 100;
  return h >= 11 && h <= 14 ? c : m === 1 ? a : m >= 2 && m <= 4 ? b : c;
};
const N = (n: number, a: string, b: string, c: string) => n + ' ' + plural(n, a, b, c);
const D = (d: string) => +d.slice(8) + ' ' + MON[+d.slice(5, 7) - 1].slice(0, 3);
const DW = (d: string) => RU[dowMon(d)] + ' ' + D(d);
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 864e5);
function range(ds: string[]) {
  if (!ds.length) return '';
  const a = ds[0],
    b = ds[ds.length - 1];
  if (a === b) return D(a);
  return a.slice(0, 7) === b.slice(0, 7) ? +a.slice(8) + '–' + D(b) : D(a) + ' – ' + D(b);
}
const whoId = (w: Who) => ('clientId' in w ? w.clientId : w.groupId);
const RULES = [
  ['src', 'Как в источнике', 'Те же промежутки между днями'],
  ['week', 'Те же дни недели', 'Пн, Ср, Пт остаются Пн, Ср, Пт'],
  ['daily', 'Подряд', 'Каждый день без пропусков'],
  ['alt', 'Через день', 'День отдыха между тренировками'],
] as const;
type Rule = (typeof RULES)[number][0];
function place(ds: string[], first: string, rule: Rule) {
  const base = ds[0];
  if (rule === 'week') {
    const w = Math.ceil(daysBetween(base, first) / 7);
    return ds.map((s) => ({ src: s, dst: addDays(s, 7 * w) }));
  }
  return ds.map((s, k) => ({ src: s, dst: addDays(first, rule === 'daily' ? k : rule === 'alt' ? 2 * k : daysBetween(base, s)) }));
}
/* Занятые дни при копии и переносе: заменить (по умолчанию), пропустить или перенести на дату.
   Переносится только эта (ставимая) тренировка — то, что уже стоит у человека, остаётся; остальные
   тренировки серии встают по раскладке. Всю серию двигает «С какой даты». Ключ — «получатель|исходная дата». */
type G = { mode: 'copy' | 'move'; targets: Who[]; first: string; rule: Rule; confirm: boolean; skip: Record<string, true>; moves: Record<string, string> };
const skipKey = (w: Who, src: string) => whoId(w) + '|' + src;
const pairsOf = (ds: string[], g: G, w: Who) => place(ds, g.first, g.rule).map((x) => ({ ...x, dst: g.moves[skipKey(w, x.src)] ?? x.dst }));
const defaultFirst = (ds: string[]) => addDays(ds[0], 7 * Math.ceil((daysBetween(ds[0], ds[ds.length - 1]) + 1) / 7));
const toWho = (w: { kind: 'client' | 'group'; id: string }): Who => (w.kind === 'group' ? { groupId: w.id } : { clientId: w.id });
type UndoItem = { who: Who; date: string; prev: { content: WorkoutContent; status: 'draft' | 'published' } | null };

export interface BulkApi {
  toggle: (date: string, shift: boolean) => void;
  picked: (date: string) => void;
}

interface Props {
  who: Who;
  whoName: string;
  isGroup: boolean;
  sel: Set<string>;
  setSel: (s: Set<string>) => void;
  picking: boolean;
  setPicking: (b: boolean) => void;
  decide: (dates: string[], publish: boolean) => Promise<Decisions | null | undefined>;
}

export const Bulk = forwardRef<BulkApi, Props>(function Bulk(p, ref) {
  const utils = trpc.useUtils();
  const W = useWhoList();
  const catalog = useCatalog();
  const [anchor, setAnchor] = useState<string | null>(null);
  const [dlg, setDlg] = useState<null | G>(null);
  /* Окно занятых дней участников, когда копируем в группу */
  const [gconf, setGconf] = useState<{ list: Conflict[]; resolve: (d: Decisions | null) => void } | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [left, setLeft] = useState(0);
  const apply = trpc.trainer.bulkApply.useMutation();
  const undoM = trpc.trainer.bulkUndo.useMutation();
  const statusM = trpc.trainer.bulkStatus.useMutation();
  const delM = trpc.trainer.bulkDelete.useMutation();
  const ds = [...p.sel].sort();

  useImperativeHandle(ref, () => ({
    toggle(date, shift) {
      const next = new Set(p.sel);
      const on = !next.has(date);
      if (shift && anchor && anchor !== date) {
        const [a, b] = anchor < date ? [anchor, date] : [date, anchor];
        /* В диапазон — только дни с тренировкой (у них есть галочка) */
        document.querySelectorAll<HTMLElement>('.bk-box[data-bk]').forEach((el) => {
          const d = el.dataset.bk!;
          if (d >= a && d <= b) on ? next.add(d) : next.delete(d);
        });
      } else on ? next.add(date) : next.delete(date);
      setAnchor(date);
      p.setSel(next);
    },
    picked(date) {
      p.setPicking(false);
      setDlg((g) => (g ? { ...g, first: date, confirm: false, skip: {}, moves: {} } : g));
    },
  }));

  const on = p.picking || p.sel.size > 0;
  useEffect(() => {
    document.body.classList.toggle('bk-on', on);
    document.body.classList.toggle('bk-picking', p.picking);
    return () => document.body.classList.remove('bk-on', 'bk-picking');
  }, [on, p.picking]);
  useLayoutEffect(() => {
    const place = () => {
      const m = document.querySelector('.main');
      if (m) {
        const r = m.getBoundingClientRect();
        setLeft(Math.round(r.left + r.width / 2));
      }
    };
    place();
    addEventListener('resize', place);
    return () => removeEventListener('resize', place);
  }, [on]);
  /* Esc снимает выделение / закрывает окно */
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('.sug')) return;
      if (confirmDel) return setConfirmDel(false);
      if (dlg && !p.picking) return setDlg(null);
      if (p.picking) return p.setPicking(false);
      const t = e.target as HTMLElement;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (p.sel.size) p.setSel(new Set());
    };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  });

  const refresh = () => utils.trainer.days.invalidate();
  const withUndo = (text: string, undo: UndoItem[]) =>
    toast(text, 'Отменить', async () => {
      await undoM.mutateAsync({ undo });
      await refresh();
      toast('Действие отменено');
    });

  const doDelete = async () => {
    setConfirmDel(false);
    const r = await delM.mutateAsync({ who: p.who, dates: ds });
    p.setSel(new Set());
    await refresh();
    withUndo('Удалено: ' + N(ds.length, 'тренировка', 'тренировки', 'тренировок'), r.undo as UndoItem[]);
  };
  const doStatus = async (pub: boolean) => {
    const rows = await utils.trainer.days.fetch({ who: p.who, from: ds[0], to: ds[ds.length - 1] });
    const todo = rows.filter((d) => p.sel.has(d.date) && (d.content.blocks.some(blockHas) || d.content.comp) && (pub ? d.status !== 'published' : d.status === 'published')).map((d) => d.date);
    if (!todo.length) return toast(pub ? 'Все выбранные уже опубликованы' : 'Все выбранные уже в черновиках');
    const decisions = await p.decide(todo, pub);
    if (decisions === null) return;
    await statusM.mutateAsync({ who: p.who, dates: todo, published: pub, decisions });
    await refresh();
    toast((pub ? 'Опубликовано: ' : 'В черновик: ') + N(todo.length, 'тренировка', 'тренировки', 'тренировок'), 'Отменить', async () => {
      await statusM.mutateAsync({ who: p.who, dates: todo, published: !pub });
      await refresh();
      toast('Действие отменено');
    });
  };
  const doApply = async (preview: Preview) => {
    if (!dlg) return;
    const g = dlg;
    const items = g.targets.flatMap((to) => pairsOf(ds, g, to).filter(({ src }) => !g.skip[skipKey(to, src)]).map(({ src, dst }) => ({ src, dst, to })));
    if (!items.length) return toast('Все тренировки пропущены — нечего ' + (g.mode === 'move' ? 'перемещать' : 'копировать'));
    /* Копия в группу: у участников на эти даты могут стоять свои тренировки — то же окно, что при публикации группы */
    const decisions: Record<string, Decisions> = {};
    const pubSrc = new Set(preview.rows.flatMap((x) => x.rows.filter((r) => r.pub).map((r) => r.s)));
    for (const to of g.targets) {
      if (!('groupId' in to)) continue;
      const dates = items.filter((i) => i.to === to && pubSrc.has(i.src)).map((i) => i.dst);
      if (!dates.length) continue;
      const list = await utils.trainer.groupConflicts.fetch({ groupId: to.groupId, dates });
      if (!list.length) continue;
      const d = await new Promise<Decisions | null>((resolve) => setGconf({ list, resolve }));
      if (d === null) return;
      decisions[to.groupId] = d;
    }
    setDlg(null);
    const r = await apply.mutateAsync({ op: g.mode, from: p.who, items, decisions });
    p.setSel(new Set());
    await refresh();
    const n = items.length;
    const pubN = items.filter((i) => pubSrc.has(i.src)).length;
    const skipped = g.targets.length * ds.length - n;
    const tgt = g.targets.length > 1 ? ' · ' + N(g.targets.length, 'клиенту', 'клиентам', 'клиентам') : whoId(g.targets[0]) !== whoId(p.who) ? ' · ' + (W.byId(whoId(g.targets[0]))?.name ?? '') : '';
    withUndo(
      (g.mode === 'move' ? 'Перемещено: ' : 'Скопировано: ') + N(n, 'тренировка', 'тренировки', 'тренировок') + tgt + (pubN ? ' · опубликовано: ' + pubN : ' · черновики') + (skipped ? ' · пропущено: ' + skipped : ''),
      r.undo as UndoItem[],
    );
  };

  return (
    <>
      {createPortal(
        <div id="bkBar" className="bk-bar" style={{ left }}>
          {on &&
            (p.picking ? (
              <>
                <span className="bk-pickmsg">
                  <Svg src={I.cal} />
                  Кликните день, с которого вставить
                </span>
                <span className="bk-sep"></span>
                <button data-bka="pickcancel" onClick={() => p.setPicking(false)}>
                  <Svg src={I.x} />
                  Отмена
                </button>
              </>
            ) : (
              <>
                <span className="bk-n">
                  <b>Выбрано {ds.length}</b>
                  <s>{range(ds)}</s>
                </span>
                <span className="bk-sep"></span>
                <button data-bka="copy" onClick={() => setDlg({ mode: 'copy', targets: [p.who], first: defaultFirst(ds), rule: 'src', confirm: false, skip: {}, moves: {} })}>
                  <Svg src={I.copy} />
                  Копировать
                </button>
                <button data-bka="move" onClick={() => setDlg({ mode: 'move', targets: [p.who], first: defaultFirst(ds), rule: 'src', confirm: false, skip: {}, moves: {} })}>
                  <Svg src={I.move} />
                  Переместить
                </button>
                <button data-bka="pub" onClick={() => doStatus(true)}>
                  <Svg src={DAYICON.pub} />
                  Опубликовать
                </button>
                <button data-bka="draft" onClick={() => doStatus(false)}>
                  <Svg src={DAYICON.draft} />В черновик
                </button>
                <button data-bka="del" className="rm" onClick={() => setConfirmDel(true)}>
                  <Svg src={I.trash} />
                  Удалить
                </button>
                <span className="bk-sep"></span>
                <button data-bka="clear" className="clr" onClick={() => p.setSel(new Set())}>
                  <Svg src={I.x} />
                  Снять выделение
                </button>
              </>
            ))}
        </div>,
        document.body,
      )}
      {confirmDel && <ConfirmDelete who={p.who} name={p.whoName} ds={ds} onOk={doDelete} onClose={() => setConfirmDel(false)} />}
      {dlg && !p.picking && <Dialog g={dlg} setG={setDlg} src={p.who} srcName={p.whoName} ds={ds} onPick={() => p.setPicking(true)} onApply={doApply} catalog={catalog} />}
      {gconf && (
        <GroupConflicts
          list={gconf.list}
          names={Object.fromEntries(W.clients.map((c) => [c.id, c.name]))}
          onDone={(d) => {
            gconf.resolve(d);
            setGconf(null);
          }}
        />
      )}
    </>
  );
});

function ConfirmDelete({ who, name, ds, onOk, onClose }: { who: Who; name: string; ds: string[]; onOk: () => void; onClose: () => void }) {
  const q = trpc.trainer.days.useQuery({ who, from: ds[0], to: ds[ds.length - 1] });
  const pub = (q.data || []).filter((d) => ds.includes(d.date) && d.status === 'published').length;
  return createPortal(
    <div className="ov on bk-confirm" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="md ask">
        <div className="mdh">
          <span className="dot"></span>
          <h2>Удалить {N(ds.length, 'тренировку', 'тренировки', 'тренировок')}?</h2>
          <button className="cls" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="mdb">
          <p className="lead">
            {name} · {range(ds)}
          </p>
          <p className="sub">{pub ? `Опубликовано из них: ${pub}. Клиент перестанет их видеть.` : 'Все выбранные — черновики, клиент их не видит.'}</p>
        </div>
        <div className="mdf">
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn rm" onClick={onOk}>
            Удалить
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

type PRow = { s: string; dst: string; conflict: boolean; past: boolean; noPm: string[]; pub?: boolean; title: string; skip: boolean; moved: boolean; name: string };
type Preview = { rows: { who: Who; rows: PRow[] }[] };

function Dialog(props: {
  g: G;
  setG: (g: G | null) => void;
  src: Who;
  srcName: string;
  ds: string[];
  onPick: () => void;
  onApply: (p: Preview) => void;
  catalog: ReturnType<typeof useCatalog>;
}) {
  const { g, setG, src, ds } = props;
  const W = useWhoList();
  const utils = trpc.useUtils();
  const move = g.mode === 'move';
  const [addEl, setAddEl] = useState<HTMLElement | null>(null);
  const [dateEl, setDateEl] = useState<HTMLElement | null>(null);
  /* Выбор даты переноса для занятого дня */
  const [mvEl, setMvEl] = useState<{ el: HTMLElement; w: Who; r: PRow } | null>(null);
  const [preview, setPreview] = useState<Preview>({ rows: [] });
  const t = today();

  /* Итог: замены, даты в прошлом, у кого нет нужного 1ПМ (CON-22) */
  useEffect(() => {
    let off = false;
    (async () => {
      const srcDays = await utils.trainer.days.fetch({ who: src, from: ds[0], to: ds[ds.length - 1] });
      const srcMap = new Map(srcDays.map((d) => [d.date, d]));
      const moved = new Set(ds);
      const out: Preview['rows'] = [];
      for (const w of g.targets) {
        const pairs = pairsOf(ds, g, w);
        const same = whoId(w) === whoId(src);
        const td = await utils.trainer.days.fetch({ who: w, from: pairs[0].dst < pairs[pairs.length - 1].dst ? pairs[0].dst : pairs[pairs.length - 1].dst, to: pairs.reduce((a, b) => (a > b.dst ? a : b.dst), pairs[0].dst) });
        const tmap = new Map(td.map((d) => [d.date, d]));
        const pm = 'clientId' in w && !same ? (await utils.trainer.maxesFor.fetch(w))[w.clientId] || {} : null;
        out.push({
          who: w,
          rows: pairs.map(({ src: s, dst }) => {
            const occ = tmap.get(dst);
            const isSame = move && same && s === dst;
            /* На эту дату встаёт и другая тренировка серии (перенесли одну на место другой) */
            const twin = pairs.find((y) => y.dst === dst && y.src < s && !g.skip[skipKey(w, y.src)]);
            const conflict = (!isSame && !!occ && (occ.content.blocks.some(blockHas) || !!occ.content.comp) && !(move && same && moved.has(dst))) || !!twin;
            const x = srcMap.get(s)?.content;
            const noPm = new Set<string>();
            if (pm && x)
              x.blocks.forEach((b) =>
                b.items.flatMap((y) => (y.t === 'set' ? y.items : [y])).forEach((y: Line) => {
                  if (y.load?.kind !== 'pct') return;
                  const k = lineKey(y, pm, props.catalog.ex);
                  if (k && !pm[k]) noPm.add(k);
                }),
              );
            return {
              s,
              dst,
              conflict,
              past: dst < t,
              noPm: [...noPm],
              pub: srcMap.get(s)?.status === 'published',
              title: twin ? srcMap.get(twin.src)?.content.title || 'тренировка серии' : occ?.content.title || 'Без названия',
              skip: !!g.skip[skipKey(w, s)],
              moved: !!g.moves[skipKey(w, s)],
              name: x?.title || 'Без названия',
            };
          }),
        });
      }
      if (!off) setPreview({ rows: out });
    })();
    return () => {
      off = true;
    };
  }, [g.first, g.rule, JSON.stringify(g.targets), JSON.stringify(g.moves), JSON.stringify(g.skip)]);

  const total = preview.rows.reduce((a, x) => a + x.rows.length, 0);
  const conf = preview.rows.reduce((a, x) => a + x.rows.filter((r) => r.conflict && !r.skip).length, 0);
  const skipN = preview.rows.reduce((a, x) => a + x.rows.filter((r) => r.skip).length, 0);
  const past = preview.rows.reduce((a, x) => a + x.rows.filter((r) => r.past && !r.skip).length, 0);
  const pubs = preview.rows.reduce((a, x) => a + x.rows.filter((r) => r.pub && !r.skip).length, 0);
  const noPm = [...new Set(preview.rows.flatMap((x) => x.rows.flatMap((r) => r.noPm.map(() => whoId(x.who)))))];
  const busyN = preview.rows.reduce((a, x) => a + x.rows.filter((r) => r.conflict || r.skip || r.moved).length, 0);
  const setDec = (w: Who, r: PRow, v: 'replace' | 'skip') => {
    const k = skipKey(w, r.s);
    const skip = { ...g.skip };
    if (v === 'skip') skip[k] = true;
    else delete skip[k];
    /* Заменить/пропустить перенесённую — она возвращается на свою дату по раскладке */
    const moves = { ...g.moves };
    delete moves[k];
    setG({ ...g, skip, moves });
  };
  const moveTo = (w: Who, r: PRow, d: string) => {
    const k = skipKey(w, r.s);
    const skip = { ...g.skip };
    delete skip[k];
    const moves = { ...g.moves };
    const base = place(ds, g.first, g.rule).find((x) => x.src === r.s)?.dst;
    if (d === base) delete moves[k];
    else moves[k] = d;
    setG({ ...g, skip, moves });
  };
  const verb = move ? 'Переместить' : 'Копировать';
  const srcW = W.byId(whoId(src));
  const av = (id: string) => {
    const w = W.byId(id);
    return <Cav ini={w?.ini} photo={w?.kind === 'client' ? w.photo : null} grp={w?.kind === 'group'} />;
  };
  return createPortal(
    <div className="ov on bk-dlg" onClick={(e) => e.target === e.currentTarget && setG(null)}>
      <div className="md bkmd">
        <div className="mdh">
          <span className="dot"></span>
          <h2>
            {verb} {N(ds.length, 'тренировку', 'тренировки', 'тренировок')}
          </h2>
          <button className="cls" onClick={() => setG(null)}>
            ✕
          </button>
        </div>
        <div className="mdb bkd">
          <section className="bkd-step">
            <span className="bkd-num">1</span>
            <div className="bkd-body">
              <div className="bkd-t">Откуда и кому</div>
              <div className="bkd-kv">
                <span className="k">Откуда</span>
                <div className="bkd-src">
                  {av(whoId(src))}
                  <b>{props.srcName}</b>
                  <s>{range(ds)}</s>
                </div>
              </div>
              <div className="bkd-kv">
                <span className="k">Кому</span>
                <div className="bkd-to">
                  {move ? (
                    <button className="bkd-cli" onClick={(e) => setAddEl(e.currentTarget)}>
                      {av(whoId(g.targets[0]))}
                      <b>{W.byId(whoId(g.targets[0]))?.name}</b>
                      <Svg src={I.chev} />
                    </button>
                  ) : (
                    <>
                      {g.targets.map((w) => (
                        <span className="bkd-chip" key={whoId(w)}>
                          {av(whoId(w))}
                          <b>{W.byId(whoId(w))?.name}</b>
                          <button aria-label="Убрать" onClick={() => setG({ ...g, targets: g.targets.filter((x) => whoId(x) !== whoId(w)), confirm: false })}>
                            <Svg src={I.x} />
                          </button>
                        </span>
                      ))}
                      <button className="bkd-add" onClick={(e) => setAddEl(e.currentTarget)}>
                        <Svg src={I.plus} />
                        Добавить клиента или группу
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
          <section className="bkd-step">
            <span className="bkd-num">2</span>
            <div className="bkd-body">
              <div className="bkd-t">С какой даты</div>
              <div className="bkd-date">
                <button className="btn gh sm jumpb" onClick={(e) => setDateEl(e.currentTarget)}>
                  <Svg src={I.cal} />
                  <span>{DW(g.first)}</span>
                  <Svg src={I.chev} />
                </button>
                <button className="bkd-lnk" onClick={props.onPick}>
                  Выбрать в календаре
                </button>
              </div>
            </div>
          </section>
          <section className="bkd-step">
            <span className="bkd-num">3</span>
            <div className="bkd-body">
              <div className="bkd-t">Как раскладывать</div>
              <div className="bkd-rules">
                {RULES.map(([k, tt, d]) => (
                  <button key={k} className={g.rule === k ? 'on' : ''} onClick={() => setG({ ...g, rule: k, confirm: false, skip: {}, moves: {} })}>
                    <b>{tt}</b>
                    <s>{d}</s>
                  </button>
                ))}
              </div>
            </div>
          </section>
          {preview.rows.length > 0 && (
            <section className="bkd-step">
              <span className={'bkd-num' + (busyN ? ' warn' : '')}>{busyN ? <Svg src={I.warn} /> : 4}</span>
              <div className="bkd-body">
                <div className="bkd-t">Что куда встанет</div>
                {busyN > 0 && <p className="bkd-hint">Где день уже занят — заменить, пропустить или перенести ставимую тренировку на другую дату. Та, что уже стоит, остаётся; остальные — на своих местах.</p>}
                <div className="gc-list bkd-busy">
                  {preview.rows.map((x) => (
                    <div key={whoId(x.who)}>
                      {g.targets.length > 1 && (
                        <div className="bkd-who">
                          {av(whoId(x.who))}
                          <b>{W.byId(whoId(x.who))?.name}</b>
                        </div>
                      )}
                      {x.rows.map((r) => {
                        const v = r.skip ? 'skip' : r.moved && !r.conflict ? 'move' : r.conflict ? 'replace' : null;
                        return (
                          <div className={'gc-r bkd-pl' + (v === 'skip' ? ' off' : '')} key={r.s}>
                            <span className="gc-t">
                              <b>
                                <span className="bkd-from">{DW(r.s)}</span> → <span className={r.moved ? 'bkd-mv' : ''}>{DW(r.dst)}</span>
                              </b>
                              <s>
                                «{r.name}»
                                {v === 'replace' ? <em className="rm"> · там «{r.title}» — заменится</em> : null}
                                {v === 'skip' ? <em> · не ставим, там остаётся «{r.title}»</em> : null}
                                {v === 'move' ? <em className="mv"> · перенесена</em> : null}
                              </s>
                            </span>
                            {v ? (
                              <span className="seg">
                                <button className={v === 'replace' ? 'on' : ''} onClick={() => setDec(x.who, r, 'replace')}>
                                  Заменить
                                </button>
                                <button className={v === 'skip' ? 'on' : ''} onClick={() => setDec(x.who, r, 'skip')}>
                                  Пропустить
                                </button>
                                <button className={v === 'move' ? 'on' : ''} onClick={(e) => setMvEl({ el: e.currentTarget, w: x.who, r })}>
                                  Перенести
                                </button>
                              </span>
                            ) : (
                              <span className="bkd-free">свободно</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
        <div className="mdf">
          <span className="bkd-sum">
            {pubs === total ? 'Встанут опубликованными' : pubs ? `Встанут: ${total - skipN}, опубликованными — ${pubs}` : 'Встанут черновиками'}
            {pubs && pubs !== total ? '' : `: ${total - skipN}`}
            {conf ? ` · замен: ${conf}` : ''}
            {skipN ? ` · пропуск: ${skipN}` : ''}
            {past ? ` · в прошлом: ${past}` : ''}
            {noPm.length ? ` · без нужного 1ПМ: ${noPm.map((id) => W.byId(id)?.name).join(', ')}` : ''}
          </span>
          <span className="sp"></span>
          <button className="btn gh" onClick={() => setG(null)}>
            Отмена
          </button>
          <button className={'btn' + (conf ? ' rm' : '')} disabled={!g.targets.length} onClick={() => props.onApply(preview)}>
            {conf ? (move ? 'Заменить и переместить' : 'Заменить и копировать') : verb}
          </button>
        </div>
      </div>
      {addEl && (
        <ClientPicker
          anchor={addEl}
          curId={move ? whoId(g.targets[0]) : null}
          opts={move ? {} : { exclude: new Set(g.targets.map(whoId)) }}
          onPick={(w) => {
            setAddEl(null);
            setG({ ...g, targets: move ? [toWho(w)] : [...g.targets, toWho(w)], confirm: false });
          }}
          onClose={() => setAddEl(null)}
        />
      )}
      {mvEl && (
        <DatePicker
          anchor={mvEl.el}
          value={addDays(mvEl.r.dst, 1)}
          onPick={(d) => {
            const { w, r } = mvEl;
            setMvEl(null);
            moveTo(w, r, d);
          }}
          onClose={() => setMvEl(null)}
        />
      )}
      {dateEl && (
        <DatePicker
          anchor={dateEl}
          value={g.first}
          onPick={(d) => {
            setDateEl(null);
            setG({ ...g, first: d, confirm: false, skip: {}, moves: {} });
          }}
          onClose={() => setDateEl(null)}
        />
      )}
      <span hidden>{srcW?.id}</span>
    </div>,
    document.body,
  );
}
